import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Calendar, Clock, User, CreditCard, Info } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CardPaymentForm from '../components/CardPaymentForm';
import AddonPopup from '../components/AddonPopup';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4 }
};

export default function Reservation() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prestationIdFromState = location.state?.prestationId || searchParams.get('prestation');

  const [step, setStep] = useState(1);
  const [showAddonPopup, setShowAddonPopup] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [addonConfirmed, setAddonConfirmed] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [creneaux, setCreneaux] = useState([]);
  const [datesSansCreneaux, setDatesSansCreneaux] = useState({});
  const [horairesSemaine, setHorairesSemaine] = useState(null);
  const [selectedCreneau, setSelectedCreneau] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    nom: '',
    email: '',
    telephone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Générer les dates disponibles (AVEC RESTRICTION 48H)
  const generateAvailableDates = () => {
    const dates = [];
    const now = new Date();
    
    // Ajouter 48 heures (2 jours) au minimum
    const minDate = new Date(now);
    minDate.setHours(now.getHours() + 24);
    
    // Générer 14 dates à partir de minDate
    for (let i = 0; i < 14; i++) {
      const date = new Date(minDate);
      date.setDate(minDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

    const isJourFerme = (date) => {
    if (!horairesSemaine) return false;
    const dateStr = getDateStr(date);
    if (datesSansCreneaux[dateStr]) return true;
    const horaire = horairesSemaine[dateStr];
    if (!horaire) return false;
    return !horaire.ouvert;
  };

  const availableDates = useMemo(() => generateAvailableDates(), [horairesSemaine]);


  useEffect(() => {
    fetchPrestations();
    // Récupérer les horaires pour savoir quels jours sont fermés
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/horaires-publics`)
      .then(r => r.json())
      .then(data => setHorairesSemaine(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (prestationIdFromState && prestations.length > 0) {
      const prestation = prestations.find(p => p.id === prestationIdFromState);
      if (prestation) {
        setSelectedPrestation(prestation);
        setStep(2);
      }
    }
  }, [prestationIdFromState, prestations]);

  useEffect(() => {
    if (selectedDate && selectedPrestation) {
      fetchCreneaux();
    }
  }, [selectedDate, selectedPrestation]);

  useEffect(() => {
    if (!selectedPrestation || availableDates.length === 0) return;
    const checkAllDates = async () => {
      const newDatesSans = {};
      for (const date of availableDates) {
        const dateStr = getDateStr(date);
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/disponibilites?date=${dateStr}&prestation_id=${selectedPrestation.id}`
          );
          const data = await response.json();
          if (data.ferme || !data.creneaux?.some(c => c.disponible)) {
            newDatesSans[dateStr] = true;
          }
        } catch (e) {}
      }
      setDatesSansCreneaux(newDatesSans);
    };
    checkAllDates();
  }, [selectedPrestation]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const fetchPrestations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prestations`);
      const data = await response.json();
      setPrestations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setPrestations([]);
    }
  };

  const fetchCreneaux = async () => {
    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/disponibilites?date=${dateStr}&prestation_id=${selectedPrestation.id}`
      );
      const data = await response.json();
      setCreneaux(Array.isArray(data.creneaux) ? data.creneaux : []);
    } catch (error) {
      console.error('Erreur:', error);
      setCreneaux([]);
    }
  };

  const handlePaymentSuccess = (reservation) => {
    // La redirection est gérée dans CardPaymentForm
    console.log('Paiement réussi:', reservation);
  };

  const handlePaymentError = (error) => {
    alert('Erreur lors de l\'enregistrement de la carte : ' + error.message);
    setIsSubmitting(false);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
    <div data-testid="reservation-page" className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="bg-secondary/30 py-12 sm:py-16">
        <div className="container-custom text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif mb-4"
          >
            Réservation en ligne
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto"
          >
            Réservez votre soin en quelques clics
          </motion.p>
        </div>
      </section>

      <div className="container-custom py-8 sm:py-12 max-w-4xl px-4">
        {/* Progress Steps */}
        <div data-testid="booking-steps" className="flex items-center justify-center mb-8 sm:mb-12 overflow-x-auto pb-2">
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div className="flex items-center flex-shrink-0">
                <div
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-medium text-sm sm:text-base ${
                    step >= num
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  {num}
                </div>
                <div className="ml-2 hidden sm:block">
                  <p className="text-xs sm:text-sm font-medium whitespace-nowrap">
                    {num === 1 && 'Prestation'}
                    {num === 2 && 'Date & Heure'}
                    {num === 3 && 'Vos infos'}
                  </p>
                </div>
              </div>
              {num < 3 && <div className="w-8 sm:w-12 md:w-24 h-0.5 bg-border mx-2 sm:mx-4 flex-shrink-0"></div>}
            </React.Fragment>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Sélection prestation */}
          {step === 1 && (
            <motion.div key="step1" {...fadeInUp} data-testid="step-1">
              <div className="bg-white border border-border rounded-sm p-4 sm:p-6 md:p-8">
                <h2 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6">Choisissez votre prestation</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {prestations.map((prestation) => (
                    <button
                      key={prestation.id}
                      data-testid={`prestation-option-${prestation.id}`}
                      onClick={() => {
                        setSelectedPrestation(prestation);
                        setStep(2);
                      }}
                      className="text-left p-3 sm:p-4 border border-border rounded-sm hover:border-accent hover:bg-accent/5 transition-colors"
                    >
                      <h3 className="font-medium mb-2 text-sm sm:text-base">{prestation.nom}</h3>
                      <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
                        <span>{prestation.duree_minutes} min</span>
                        <span className="text-accent font-medium">{prestation.prix_euros}€</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Sélection date et créneau */}
          {step === 2 && selectedPrestation && (
            <motion.div key="step2" {...fadeInUp} data-testid="step-2">
              <div className="bg-white border border-border rounded-sm p-4 sm:p-6 md:p-8">
                <div className="mb-4 sm:mb-6">
                  <button
                    data-testid="back-to-step-1"
                    onClick={() => setStep(1)}
                    className="flex items-center text-accent hover:underline text-xs sm:text-sm mb-4"
                  >
                    <ChevronLeft size={16} />
                    <span>Changer de prestation</span>
                  </button>
                  <div className="bg-secondary/30 p-3 sm:p-4 rounded-sm">
                    <p className="text-xs sm:text-sm text-muted-foreground">Prestation sélectionnée</p>
                    <p className="font-medium text-sm sm:text-base">{selectedPrestation.nom}</p>
                  </div>
                </div>

                <h2 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6">Choisissez une date</h2>
                {!horairesSemaine && <p style={{fontSize:'10px',color:'red'}}>Horaires non chargés</p>}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-8">
                  {availableDates.map((date, index) => (
                    <button
                      key={index}
                      data-testid={`date-option-${index}`}
                      onClick={() => !isJourFerme(date) && setSelectedDate(date)}
                      disabled={isJourFerme(date)}
                      className={`p-2 sm:p-3 md:p-4 border rounded-sm text-xs sm:text-sm transition-colors ${
                        isJourFerme(date)
                          ? 'border-border bg-secondary/50 opacity-50 cursor-not-allowed'
                          : selectedDate && selectedDate.toDateString && selectedDate.toDateString() === date.toDateString()
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-accent'
                      }`}
                    >
                                            <p className="font-medium">{date.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: 'Europe/Paris' }).slice(0, 3) + '.'}</p>
                      <p className="text-xs text-muted-foreground mt-1">{date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })}</p>
                      {isJourFerme(date) && (
                        <p className="text-[9px] text-muted-foreground mt-0.5">Fermé</p>
                      )}
                    </button>
                  ))}
                </div>

                {selectedDate && (
                  <div data-testid="creneaux-container">
                    <h3 className="text-lg sm:text-xl font-serif mb-3 sm:mb-4">Créneaux disponibles</h3>
                    {creneaux.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
                        {creneaux.map((creneau, index) => (
                          <button
                            key={index}
                            data-testid={`creneau-${index}`}
                            onClick={() => creneau.disponible && setSelectedCreneau(creneau)}
                            disabled={!creneau.disponible}
                            className={`p-2 sm:p-3 border rounded-sm text-xs sm:text-sm transition-colors ${
                              !creneau.disponible
                                ? 'opacity-30 cursor-not-allowed line-through'
                                : selectedCreneau?.heure_debut === creneau.heure_debut
                                ? 'border-accent bg-accent text-accent-foreground'
                                : 'border-border hover:border-accent'
                            }`}
                          >
                            <span className="font-medium">{creneau.heure_debut}</span>
                            <span className="text-[10px] sm:text-xs opacity-70"> - {creneau.heure_fin}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-xs sm:text-sm">Chargement des créneaux...</p>
                    )}

                    {selectedCreneau && (
                      <button
                        data-testid="next-to-step-3"
                        onClick={() => { if (selectedPrestation && selectedPrestation.categorie) { setShowAddonPopup(true); } else { setStep(3); } }}
                        className="w-full btn-primary px-4 sm:px-6 py-2.5 sm:py-3 bg-accent text-accent-foreground rounded-sm font-medium text-sm sm:text-base"
                      >
                        Continuer
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Informations client ET Paiement */}
          {step === 3 && selectedCreneau && (
            <motion.div key="step3" {...fadeInUp} data-testid="step-3">
              <div className="bg-white border border-border rounded-sm p-4 sm:p-6 md:p-8">
                <button
                  data-testid="back-to-step-2"
                  onClick={() => setStep(2)}
                  className="flex items-center text-accent hover:underline text-xs sm:text-sm mb-4 sm:mb-6"
                >
                  <ChevronLeft size={16} />
                  <span>Retour</span>
                </button>

                <div className="bg-secondary/30 p-3 sm:p-4 rounded-sm mb-4 sm:mb-6">
                  <h3 className="font-medium mb-2 sm:mb-3 text-sm sm:text-base">Récapitulatif</h3>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} className="text-accent flex-shrink-0" />
                      <span className="break-words">{formatDate(selectedDate)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock size={16} className="text-accent flex-shrink-0" />
                      <span>{selectedCreneau.heure_debut} - {selectedCreneau.heure_fin}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User size={16} className="text-accent flex-shrink-0" />
                      <span className="break-words">{selectedPrestation.nom}</span>
                    </div>
                  </div>
                </div>

                <h2 className="text-xl sm:text-2xl font-serif mb-4 sm:mb-6">Vos informations</h2>

                {/* Formulaire client */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-2">Nom complet *</label>
                    <input
                      type="text"
                      required
                      value={clientInfo.nom}
                      onChange={(e) => setClientInfo({ ...clientInfo, nom: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={clientInfo.email}
                      onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      required
                      value={clientInfo.telephone}
                      onChange={(e) => setClientInfo({ ...clientInfo, telephone: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Vérifier que toutes les infos sont remplies avant d'afficher le formulaire de carte */}
                {clientInfo.nom && clientInfo.email && clientInfo.telephone && !addonConfirmed && selectedPrestation.categorie && (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowAddonPopup(true)}
                      className="w-full py-3 text-sm font-semibold tracking-widest uppercase rounded-sm mb-2"
                      style={{background:'#3d1f2a', color:'#f0dde4'}}
                    >
                      Continuer vers le paiement
                    </button>
                  </div>
                )}
                {clientInfo.nom && clientInfo.email && clientInfo.telephone && addonConfirmed ? (
                  <Elements stripe={stripePromise}>
                    <CardPaymentForm
                      reservationData={{
                        prestation_id: selectedPrestation.id,
                        nom_client: clientInfo.nom,
                        email_client: clientInfo.email,
                        telephone_client: clientInfo.telephone,
                        date: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
                        heure_debut: selectedCreneau.heure_debut,
                        heure_fin: selectedCreneau.heure_fin,
                        addons: selectedAddons.map(p => ({ id: p.id, nom: p.nom, prix: p.prix }))
                      }}
                      montantTotal={parseFloat(selectedPrestation.prix_euros) + selectedAddons.reduce((s,p) => s + parseFloat(p.prix), 0)}
                      montantAcompte={parseFloat(((parseFloat(selectedPrestation.prix_euros) + selectedAddons.reduce((s,p) => s + parseFloat(p.prix), 0)) * 0.5).toFixed(2))}
                      selectedAddons={selectedAddons}
                      selectedPrestation={selectedPrestation}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </Elements>
                ) : (
                  !addonConfirmed ? null :
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-center">
                    <p className="text-sm text-amber-800">
                      Veuillez remplir tous les champs ci-dessus pour continuer
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>

      {showAddonPopup && selectedPrestation && (
        <AddonPopup
          categorie={selectedPrestation.categorie}
          selectedAddons={selectedAddons}
          setSelectedAddons={setSelectedAddons}
          onClose={() => { setShowAddonPopup(false); setAddonConfirmed(true); setStep(3); }}
          onConfirm={() => { setShowAddonPopup(false); setAddonConfirmed(true); setStep(3); }}
        />
      )}
    </>
  );
}
